import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';

const C = {
  navy: '#0D1B2A', teal: '#00B4D8', tealDark: '#0087A8',
  bg: '#F0F7FA', success: '#10B981', warning: '#F59E0B',
  error: '#EF4444', dark: '#1F2937', grey: '#6B7280',
  border: '#E5E7EB', white: '#FFFFFF',
};

const METHODS = [
  { value: 'bank_transfer', label: '🏦 Bank Transfer', desc: 'International bank transfer' },
  { value: 'paypal', label: '💙 PayPal', desc: 'PayPal business account' },
  { value: 'wise', label: '🟢 Wise', desc: 'Wise (formerly TransferWise)' },
];

const inp = {
  width: '100%', padding: '10px 14px', borderRadius: 8,
  border: '1.5px solid #E5E7EB', fontSize: 14, color: '#1F2937',
  outline: 'none', boxSizing: 'border-box', background: '#fff',
};

export default function SuperAdminBankSettings() {
  const [form, setForm] = useState({
    payment_method: 'bank_transfer',
    bank_name: '', account_holder: '', account_number: '',
    sort_code: '', swift_bic: '', iban: '',
    paypal_email: '', wise_email: '',
    currency: 'USD', payment_notes: '',
    crypto_enabled: false, crypto_network: 'Arbitrum (USDC)', crypto_wallet: '', crypto_notes: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    api.get('/api/superadmin/bank-settings')
      .then(res => {
        if (res.data.settings) {
          setForm(f => ({ ...f, ...res.data.settings }));
        }
      })
      .catch(() => setError('Failed to load settings.'))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.post('/api/superadmin/bank-settings', form);
      setSuccess('Bank details saved! Partners can now see these details for payment.');
      setTimeout(() => setSuccess(''), 5000);
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  const method = form.payment_method;

  return (
    <Layout role="superadmin">
      <div style={{ padding: '32px 24px', maxWidth: 720, margin: '0 auto' }}>
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: C.dark, margin: 0 }}>Platform Bank Settings</h1>
          <p style={{ color: C.grey, margin: '4px 0 0', fontSize: 14 }}>
            These details are shown to partner schools so they can send subscription payments to you.
          </p>
        </div>

        {success && <div style={{ background: '#D1FAE5', border: '1px solid #6EE7B7', color: '#065F46', borderRadius: 8, padding: '12px 16px', marginBottom: 20, fontWeight: 500 }}>✓ {success}</div>}
        {error && <div style={{ background: '#FEE2E2', border: '1px solid #FCA5A5', color: '#991B1B', borderRadius: 8, padding: '12px 16px', marginBottom: 20, fontWeight: 500 }}>✗ {error}</div>}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: C.grey }}>Loading…</div>
        ) : (
          <div style={{ background: C.white, borderRadius: 16, padding: 32, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', border: `1px solid ${C.border}` }}>
            {/* Warning if no settings */}
            <div style={{ background: '#FFFBEB', border: '1px solid #FCD34D', borderRadius: 10, padding: '12px 16px', marginBottom: 24, fontSize: 13, color: '#92400E' }}>
              ⚠️ <strong>Important:</strong> Partner schools cannot pay until you add your bank details here. Fill in the details below and click Save.
            </div>

            {/* Payment method */}
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: C.dark, marginBottom: 10 }}>Payment Method</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                {METHODS.map(m => (
                  <div key={m.value}
                    onClick={() => setForm(f => ({ ...f, payment_method: m.value }))}
                    style={{
                      padding: '14px 12px', borderRadius: 10, textAlign: 'center', cursor: 'pointer',
                      border: `2px solid ${method === m.value ? C.teal : C.border}`,
                      background: method === m.value ? '#F0FBFF' : C.white,
                      transition: 'all 0.15s',
                    }}>
                    <div style={{ fontSize: 20, marginBottom: 4 }}>{m.label.split(' ')[0]}</div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: method === m.value ? C.teal : C.dark }}>{m.label.split(' ').slice(1).join(' ')}</div>
                    <div style={{ fontSize: 11, color: C.grey, marginTop: 2 }}>{m.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Bank transfer fields */}
            {method === 'bank_transfer' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <Field label="Account Holder Name *" name="account_holder" value={form.account_holder} onChange={handleChange} placeholder="John Smith" fullWidth />
                <Field label="Bank Name *" name="bank_name" value={form.bank_name} onChange={handleChange} placeholder="Barclays Bank" />
                <Field label="Currency" name="currency" value={form.currency} onChange={handleChange} placeholder="USD" />
                <Field label="Account Number / IBAN" name="account_number" value={form.account_number} onChange={handleChange} placeholder="12345678" mono />
                <Field label="IBAN (if applicable)" name="iban" value={form.iban} onChange={handleChange} placeholder="GB29 NWBK 6016 1331 9268 19" mono />
                <Field label="Sort Code" name="sort_code" value={form.sort_code} onChange={handleChange} placeholder="60-16-13" mono />
                <Field label="SWIFT / BIC" name="swift_bic" value={form.swift_bic} onChange={handleChange} placeholder="BARCGB22" mono />
              </div>
            )}

            {method === 'paypal' && (
              <Field label="PayPal Business Email *" name="paypal_email" value={form.paypal_email} onChange={handleChange} placeholder="payments@yourcompany.com" fullWidth />
            )}

            {method === 'wise' && (
              <Field label="Wise Email *" name="wise_email" value={form.wise_email} onChange={handleChange} placeholder="payments@yourcompany.com" fullWidth />
            )}

            {/* Notes */}
            <div style={{ marginTop: 20 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.dark, marginBottom: 6 }}>
                Additional Notes <span style={{ fontWeight: 400, color: C.grey }}>(optional)</span>
              </label>
              <textarea
                name="payment_notes"
                value={form.payment_notes}
                onChange={handleChange}
                placeholder="e.g. Please include school name and billing month in reference…"
                rows={3}
                style={{ ...inp, resize: 'vertical', minHeight: 80 }}
              />
            </div>

            {/* Crypto (USDC) — additional payment method, shown alongside the above */}
            <div style={{ marginTop: 24, padding: 18, border: `2px solid ${form.crypto_enabled ? '#10B981' : C.border}`, borderRadius: 12, background: form.crypto_enabled ? '#ECFDF5' : '#fff' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontWeight: 700, color: C.dark }}>
                <input type="checkbox" checked={!!form.crypto_enabled}
                  onChange={e => setForm(f => ({ ...f, crypto_enabled: e.target.checked }))}
                  style={{ width: 18, height: 18 }} />
                ₮ Also accept USDC (crypto)
              </label>
              <p style={{ fontSize: 12, color: C.grey, margin: '6px 0 0 28px' }}>Shown to partners as an extra way to pay their subscription.</p>
              {form.crypto_enabled && (
                <div style={{ marginTop: 14, display: 'grid', gap: 12 }}>
                  <Field label="Network" name="crypto_network" value={form.crypto_network} onChange={handleChange} placeholder="e.g. Arbitrum (USDC)" fullWidth />
                  <Field label="USDC Wallet Address *" name="crypto_wallet" value={form.crypto_wallet} onChange={handleChange} placeholder="0x…" fullWidth />
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.dark, marginBottom: 6 }}>Payment instructions <span style={{ fontWeight: 400, color: C.grey }}>(how to pay in crypto)</span></label>
                    <textarea name="crypto_notes" value={form.crypto_notes} onChange={handleChange} rows={3}
                      placeholder="e.g. Send the exact USD amount in USDC on the Arbitrum network to the address above. Use your school name as the memo/reference, then upload the transaction hash as proof."
                      style={{ ...inp, resize: 'vertical', minHeight: 70 }} />
                  </div>
                </div>
              )}
            </div>

            {/* Save */}
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                marginTop: 24, width: '100%', padding: '13px 24px', borderRadius: 10,
                background: saving ? C.grey : C.teal, color: C.white,
                border: 'none', fontWeight: 700, fontSize: 15, cursor: saving ? 'not-allowed' : 'pointer',
                transition: 'background 0.15s',
              }}
            >
              {saving ? 'Saving…' : '💾 Save Bank Details'}
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
}

function Field({ label, name, value, onChange, placeholder, mono, fullWidth }) {
  return (
    <div style={{ gridColumn: fullWidth ? '1 / -1' : 'auto', marginBottom: 4 }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.dark, marginBottom: 5 }}>{label}</label>
      <input
        name={name} value={value || ''} onChange={onChange}
        placeholder={placeholder}
        style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1.5px solid #E5E7EB', fontSize: 14, color: '#1F2937', outline: 'none', boxSizing: 'border-box', fontFamily: mono ? 'monospace' : 'inherit' }}
      />
    </div>
  );
}
